import {
  Box,
  Checkbox,
  makeStyles,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@material-ui/core';
import { CSSProperties, MouseEvent, useMemo } from 'react';

import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ImportExportIcon from '@material-ui/icons/ImportExport';

import clsx from 'clsx';
import { useWbTableContext } from '../context/WbTableContext';
import { TableCellProps } from '../types';

const useStyles = makeStyles(
  theme => ({
    columnResizer: {
      width: '30px',
      height: '100%',
      position: 'absolute',
      right: 0,
      top: 0,
      cursor: 'ew-resize',
      userSelect: 'none',
      touchAction: 'none',
      '&:hover $handler': {
        transform: 'translateY(-50%) scaleX(1.5) scaleY(1.2)',
        background: theme.palette.primary.main,
        width: '2px',
      },
    },
    handler: {
      width: '1px',
      height: '16px',
      background: theme.palette.grey[300],
      position: 'absolute',
      right: '20%',
      top: '50%',
      transform: 'translateY(-50%)',
      transition: 'transform 0.2s ease-in-out',
    },
    mask: {
      width: '10px',
      height: '100%',
      display: 'block',
      position: 'absolute',
      top: 0,
      right: 0,
      background: theme.palette.white,
    },
    sortIcon: {
      opacity: 0.5,
      transition: 'opacity 0.2s ease-in-out',
    },
    tableCell: {
      paddingLeft: theme.spacing(0.5),
      paddingRight: theme.spacing(1),
      marginRight: -theme.spacing(1),
      overflow: 'hidden',
      userSelect: 'none',
      touchAction: 'none',
      textAlign: 'left',
      '&:hover $sortIcon': {
        opacity: 1,
      },
      '&:hover $handler': {
        transform: 'translateY(-50%) scaleX(1.5) scaleY(1.2)',
        background: theme.palette.primary.main,
        width: '2px',
      },
    },
    sortable: {
      cursor: 'pointer',
    },
    tableSelectorCell: {
      paddingLeft: theme.spacing(0.5),
      paddingRight: theme.spacing(1),
      userSelect: 'none',
      touchAction: 'none',
      width: '50px',
      overflow: 'hidden',
      textOverflow: 'clip',
    },
  }),
  { name: 'WbTableHeader' },
);

export interface TableHeaderProps<T> {
  style?: CSSProperties;
  multiSelection?: boolean;
  selectedItems?: T[];
  setSelectedItems?: (newSelection: T[]) => void;
  className?: string;
}

const ColumnResizer = ({ ...props }) => {
  const classes = useStyles();
  return (
    <div {...props} className={classes.columnResizer}>
      <div className={classes.mask} />
      <div className={classes.handler} />
    </div>
  );
};

function Selector<T>({ selectableRows }: { selectableRows: T[] }) {
  const { selectedItems, setSelectedItems } = useWbTableContext<T>();

  return (
    <Checkbox
      disabled={
        selectableRows.length === 0 ||
        (selectedItems && selectableRows.length < selectedItems?.length)
      }
      indeterminate={
        (selectedItems &&
          selectedItems?.length > 0 &&
          selectedItems?.length !== selectableRows.length) ||
        selectableRows.length === 0
      }
      onClick={() => {
        if (selectedItems!.length === selectableRows.length) {
          setSelectedItems!([]);
        } else {
          setSelectedItems!(selectableRows);
        }
      }}
      checked={selectedItems?.length === selectableRows.length}
      color="primary"
      inputProps={{ 'aria-label': 'select all' }}
    />
  );
}

const SortingIcon = ({
  enable,
  sortDirection,
}: {
  enable: boolean;
  sortDirection: string | null;
}): React.ReactElement => {
  const classes = useStyles();

  if (!enable) return <></>;

  if (sortDirection === 'asc')
    return <ArrowUpwardIcon className={classes.sortIcon} fontSize="small" />;

  if (sortDirection === 'desc')
    return <ArrowDownwardIcon className={classes.sortIcon} fontSize="small" />;

  return <ImportExportIcon className={classes.sortIcon} fontSize="small" />;
};

export function TableHeader<T>({ style, className }: TableHeaderProps<T>) {
  const {
    rows,
    checkIfDisabled,
    tableSettings,
    columns,
    selection,
    tooltipIfDisabled,
  } = useWbTableContext<T>();

  const theme = useTheme();
  const classes = useStyles();

  const selectableRows = useMemo(() => {
    return rows.filter(r => !checkIfDisabled?.(r));
  }, [rows, checkIfDisabled]);

  const getTableCellComponent = (cell: TableCellProps<T>) => {
    const component = (
      <Typography color="primary" component="div" variant="button">
        <Box
          style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}
          fontSize={theme.typography.body2.fontSize}
        >
          {cell.headerName}
        </Box>
      </Typography>
    );

    if (!!cell.headerDescription) {
      return <Tooltip title={cell.headerDescription} children={component} />;
    }

    return component;
  };

  return (
    <TableHead
      style={{ background: theme.palette.background.default, ...style }}
      className={className}
    >
      <TableRow>
        {selection === 'multiple' && (
          <TableCell
            className={classes.tableSelectorCell}
            key="all-cell-selector"
          >
            {tooltipIfDisabled && selectableRows.length === 0 ? (
              <Tooltip title={tooltipIfDisabled}>
                <span>
                  <Selector selectableRows={selectableRows} />
                </span>
              </Tooltip>
            ) : (
              <Selector selectableRows={selectableRows} />
            )}
          </TableCell>
        )}
        {columns.map((cell, cellIndex) => {
          const uniqueFieldId =
            cell.uniqueFieldId || String(cell.field).replaceAll('.', '_');

          const column = tableSettings.getColumn(uniqueFieldId);

          const header = tableSettings
            .getFlatHeaders()
            .find((h: { id: string }) => h.id === column.id);

          return (
            <TableCell
              className={clsx(
                classes.tableCell,
                column.columnDef.enableSorting && classes.sortable,
              )}
              key={`header-cell-index-${cellIndex}`}
              onClick={
                column.columnDef.enableSorting
                  ? column.getToggleSortingHandler()
                  : () => {}
              }
              style={{
                width:
                  cellIndex === columns.length - 1
                    ? 'auto'
                    : `calc(var(--header-${uniqueFieldId}-${cellIndex}-size) * 1px)`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {getTableCellComponent(cell)}
                <SortingIcon
                  enable={column.columnDef.enableSorting}
                  sortDirection={header.column.getIsSorted()}
                />
              </div>

              {column.columnDef.enableResizing && (
                <ColumnResizer
                  {...{
                    onClick: (e: MouseEvent) => {
                      e.stopPropagation();
                    },
                    onMouseDown: header.getResizeHandler(),
                    onTouchStart: header.getResizeHandler(),
                  }}
                />
              )}
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
}
